/**
 * Team Design — team-logo overlay.
 *
 * Mirrors the team-name overlay system but for an uploaded logo image.
 * Combines render + auto-load + orchestrator in a single file since the
 * logo pipeline is simpler (no input field, no fade animation).
 *
 * Owns:
 *  - injectLogo()              — inserts/clears an <image> + clipPath into
 *                                the live SVG at the #logo placeholder
 *  - applyLogoToCard()         — loads the colored SVG (if needed) and
 *                                overlays the logo, guarded against hover
 *  - IntersectionObserver      — applies the logo to every card that scrolls
 *                                into view
 *  - Grid MutationObserver     — handles SVG swaps, swatch population, and
 *                                new cards added by pagination / AJAX
 *  - Thumbnail-strip handler   — restores the logo SVG on thumb-leave when
 *                                the text system isn't already doing it
 *  - Upload events (teamLogoUploaded / teamLogoRemoved)
 *
 * Depends on:
 *   window.TeamDesignSvg                 (fetch + colorize)
 *   window.CollectionTeamText.{state,dom} (render layer)
 *
 * Exposes window.CollectionTeamText.logo.
 */
(function () {
  'use strict';

  var NS    = (window.CollectionTeamText = window.CollectionTeamText || {});
  var state = (NS.state = NS.state || {
    currentText:  '',
    autoMode:     false,
    visibleCards: new Set(),
  });
  if (!('currentLogoUrl' in state)) state.currentLogoUrl = null;

  var SvgEngine        = window.TeamDesignSvg;
  var INJECTED_LOGO_ID = 'injectedTeamLogo';

  // Independent visibility tracking so we don't depend on the text system's
  // state.visibleCards (which is only populated while team-name is active).
  var logoVisibleCards = new Set();
  var logoIntersectObs = null;

  // ─── DOM shims (fall back if the text render layer isn't loaded) ─────────
  function getPreview(card) {
    return NS.dom ? NS.dom.getPreview(card) : card.querySelector('.card-design-preview');
  }
  function getMediaWrap(card) {
    return NS.dom ? NS.dom.getMediaWrap(card)
                  : card.querySelector('.card-product-figma__image-frame .media--transparent');
  }
  function getFirstSwatch(card) {
    return NS.dom ? NS.dom.getFirstSwatch(card) : card.querySelector('.card-color-swatch');
  }
  function findSvg(previewEl) {
    return previewEl && previewEl.querySelector ? previewEl.querySelector('svg') : null;
  }

  // ─── Logo rect — matches the #logo placeholder exactly ──────────────────
  function readLogoRect(svg) {
    var el = svg.querySelector('#logo');
    if (!el) return null;
    var x = parseFloat(el.getAttribute('x'));
    var y = parseFloat(el.getAttribute('y'));
    var w = parseFloat(el.getAttribute('width'));
    var h = parseFloat(el.getAttribute('height'));
    if (isNaN(x) || isNaN(y) || isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return null;
    return { x: x, y: y, w: w, h: h };
  }

  // ─── Inject / remove logo <image> in the SVG ─────────────────────────────
  function injectLogo(previewEl) {
    var svg = findSvg(previewEl);
    if (!svg) return;

    var prior = svg.querySelector('#' + INJECTED_LOGO_ID);
    if (prior && prior.parentNode) prior.parentNode.removeChild(prior);

    var originalGroup = svg.querySelector('#logoText');
    if (originalGroup && !previewEl._savedLogoText) {
      previewEl._savedLogoText = originalGroup.cloneNode(true);
    }

    if (!state.currentLogoUrl) {
      if (!originalGroup && previewEl._savedLogoText) {
        svg.appendChild(previewEl._savedLogoText.cloneNode(true));
      }
      return;
    }

    if (originalGroup && originalGroup.parentNode) {
      originalGroup.parentNode.removeChild(originalGroup);
    }

    var rect = readLogoRect(svg);
    if (!rect) return;

    var ns = 'http://www.w3.org/2000/svg';

    // Fit-by-height with bounded-width — restores the pre-refactor behavior
    // and adds an explicit overhang cap. The logo's height is the primary
    // fit axis (matches rect.h); the bbox we hand to <image> is wider than
    // the rect itself (up to MAX_LOGO_WIDTH_MULTIPLIER × rect.w), centered
    // horizontally on rect's center. `preserveAspectRatio='xMidYMid meet'`
    // then picks the scale that fits the image inside the bbox:
    //   - Tall / square / mildly-wide logos hit the height limit first →
    //     displayed at height = rect.h, width = aspect × rect.h (may
    //     overhang the rect horizontally — that's the point).
    //   - Very wide / banner logos hit the width cap → displayed at
    //     width = maxW, height = maxW / aspect (< rect.h), centered
    //     vertically inside the bbox.
    //
    // Previous behavior (image bbox = rect.w × rect.h with meet) shrank
    // wide logos to fit rect.w, leaving them visually small inside a tall
    // placeholder. The multiplier is the dial — bumping it up admits even
    // wider logos at full height; pulling it back down clamps the overhang.
    var MAX_LOGO_WIDTH_MULTIPLIER = 2;
    var maxLogoWidth = rect.w * MAX_LOGO_WIDTH_MULTIPLIER;
    var img          = document.createElementNS(ns, 'image');
    img.setAttribute('id',                  INJECTED_LOGO_ID);
    img.setAttribute('x',                   rect.x + (rect.w - maxLogoWidth) / 2);
    img.setAttribute('y',                   rect.y);
    img.setAttribute('width',               maxLogoWidth);
    img.setAttribute('height',              rect.h);
    img.setAttribute('href',                state.currentLogoUrl);
    img.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Paint behind the shadow overlay so the logo sits under the jersey shading.
    // SVG paint order = document order; earlier siblings render first.
    // Some source SVGs have a trailing space in the overlay's id/class
    // (e.g. `id="overlayShadow "`), so #overlayShadow alone misses them —
    // try the trailing-space variant and the class selector as fallbacks.
    var overlayShadow = svg.querySelector('#overlayShadow') ||
                        svg.querySelector('[id^="overlayShadow"]') ||
                        svg.querySelector('.overlayShadow');
    if (overlayShadow && overlayShadow.parentNode) {
      overlayShadow.parentNode.insertBefore(img, overlayShadow);
    } else {
      svg.appendChild(img);
    }
  }

  function refreshAllVisibleLogos() {
    var previews = document.querySelectorAll('.card-design-preview');
    for (var i = 0; i < previews.length; i++) {
      var el = previews[i];
      if (el.style.display !== 'none' && el.firstChild) {
        injectLogo(el);
      }
    }
  }

  // ─── Auto-load SVG on a card that doesn't have it yet ────────────────────
  // Mirrors renderAutoPreview() in team-design.text-auto.js.
  function applyLogoToCard(card) {
    if (!state.currentLogoUrl) return;
    // Gate on `state.armed` so cookie-restored logo doesn't auto-paint
    // every visible card on page load. arm() flips this from the four
    // interaction sites (see team-design.text-render.js).
    if (!state.armed) return;
    if (card.querySelector('.card-color-swatch--active')) return;
    if (card._thumbHover) return;

    var previewEl = getPreview(card);
    if (!previewEl) return;

    // SVG already rendered — overlay the logo directly.
    if (previewEl.firstChild) {
      injectLogo(previewEl);
      return;
    }

    var swatch = getFirstSwatch(card);
    if (!swatch) return;
    var thumbnail = swatch.dataset.thumbnail || '';
    if (!thumbnail || !SvgEngine) return;

    // Fetch shares the engine's in-memory cache — no duplicate requests.
    SvgEngine.fetch(thumbnail).then(function (svgText) {
      if (!svgText || !state.currentLogoUrl)    return;
      if (!logoVisibleCards.has(card))           return; // scrolled away
      if (card.querySelector('.card-color-swatch--active')) return;
      if (card._thumbHover)                      return;

      var theme = {
        primary:   swatch.dataset.primary   || '',
        secondary: swatch.dataset.secondary || '',
        tertiary:  swatch.dataset.tertiary  || '',
        accent:    swatch.dataset.accent    || '',
      };
      var colored   = SvgEngine.colorize(svgText, theme);
      var mediaWrap = getMediaWrap(card);
      if (mediaWrap) mediaWrap.style.visibility = 'hidden';
      previewEl.dataset.logoAutoLoaded = '1';
      previewEl.dataset.autoLoaded     = '1'; // compat with text clear logic
      previewEl.innerHTML              = colored;
      previewEl.style.display          = 'block';
      // Grid MutationObserver fires after innerHTML → injectLogo runs automatically.
    });
  }

  function applyLogoToVisibleCards() {
    logoVisibleCards.forEach(applyLogoToCard);
  }

  // ─── Clear cards whose SVG was loaded solely for the logo ────────────────
  function clearLogoOnlyCards() {
    var previews = document.querySelectorAll('.card-design-preview[data-logo-auto-loaded]');
    for (var i = 0; i < previews.length; i++) {
      var previewEl = previews[i];
      // Keep the SVG visible when ANY other customization intent is still
      // active — team-name typed, banner color picked, or banner font
      // pair picked. Without these checks, removing the logo would yank
      // the colorized jersey + "TEAM NAME" placeholder away even though
      // the shopper still has picker selections they presumably want to
      // see.
      if ((state.autoMode && state.currentText) ||
          state.bannerTheme ||
          state.bannerTextFont ||
          state.bannerNumberFont) {
        delete previewEl.dataset.logoAutoLoaded;
        continue;
      }
      var card      = previewEl.closest ? previewEl.closest('.card-wrapper') : null;
      var mediaWrap = card ? getMediaWrap(card) : null;
      delete previewEl.dataset.logoAutoLoaded;
      delete previewEl.dataset.autoLoaded;
      previewEl._autoColoredSvg = null;
      previewEl.style.display   = 'none';
      previewEl.innerHTML       = '';
      if (mediaWrap) mediaWrap.style.visibility = '';
    }
  }

  // ─── Thumbnail-strip hover — restore logo SVG on mouse-leave ────────────
  // team-design.text-auto.js hides the preview on thumb mouseenter but only
  // restores it when team-name text is active. When only a logo is uploaded
  // (no team name), we must restore it ourselves on mouseleave.
  function attachLogoThumbnailHandlers(card) {
    var thumbOuter = card.querySelector('.card-product-figma__thumb-outer');
    if (!thumbOuter || thumbOuter._logoThumbHandled) return;
    thumbOuter._logoThumbHandled = true;

    thumbOuter.addEventListener('mouseenter', function () {
      card._thumbHover = true;
      // If the text system is also active it owns the hide/show cycle.
      if (state.autoMode && state.currentText) return;
      var previewEl = getPreview(card);
      // autoLoaded covers both logo-only loads and swatch-leave restores.
      if (!previewEl || previewEl.dataset.autoLoaded !== '1') return;
      var mediaWrap = getMediaWrap(card);
      previewEl.style.display = 'none';
      if (mediaWrap) mediaWrap.style.visibility = '';
    });

    thumbOuter.addEventListener('mouseleave', function () {
      card._thumbHover = false;
      if (!state.currentLogoUrl) return;
      // Both text + logo active: the text system's mouseleave re-applies the
      // colored SVG (innerHTML swap) which fires the MutationObserver →
      // injectLogo. Nothing to do here.
      if (state.autoMode && state.currentText) return;
      var previewEl = getPreview(card);
      if (!previewEl || !previewEl.firstChild) return;
      // mouseenter only flipped display:none — the SVG (and its overlays) are
      // still in the DOM. Restore visibility. We don't gate on the
      // logoAutoLoaded flag because depending on which auto-loader won the
      // initial race (text vs logo vs swatch-leave restore) the flag may not
      // be set even though the SVG is present and we own the hide/show cycle.
      var mediaWrap = getMediaWrap(card);
      if (mediaWrap) mediaWrap.style.visibility = 'hidden';
      previewEl.style.display = 'block';
      injectLogo(previewEl);
    });
  }

  // ─── IntersectionObserver — mirrors setupIntersectionObserver() in text.js ─
  function observeAllLogoCards() {
    if (!logoIntersectObs) return;
    var cards = document.querySelectorAll('.card-wrapper');
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i]._logoObserved) {
        cards[i]._logoObserved = true;
        logoIntersectObs.observe(cards[i]);
      }
      attachLogoThumbnailHandlers(cards[i]);
    }
  }

  function setupIntersectionObserver() {
    if (typeof IntersectionObserver === 'undefined') return;
    logoIntersectObs = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var card  = entry.target;
        if (entry.isIntersecting) {
          logoVisibleCards.add(card);
          if (state.currentLogoUrl) applyLogoToCard(card);
        } else {
          logoVisibleCards.delete(card);
        }
      }
    }, { rootMargin: '100px 0px' });

    observeAllLogoCards();
  }

  // ─── Grid MutationObserver — SVG swaps + swatch population + new cards ──
  // Mirrors startPreviewObserver() in team-design.text.js.
  function startGridObserver() {
    var root = document.querySelector('.product-grid-container') ||
               document.getElementById('product-grid') ||
               document.body;
    if (!root) return;

    var observer = new MutationObserver(function (mutations) {
      var processedPreview = null;
      var sawCardAddition  = false;

      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (!t || !t.classList) continue;

        if (t.classList.contains('card-design-preview')) {
          // Deduplicate — same element can appear in multiple mutation records.
          if (processedPreview === t) continue;
          processedPreview = t;
          // Fresh SVG — reset saved snapshot so injectLogo re-reads #logoText.
          t._savedLogoText = null;
          injectLogo(t);
        } else if (t.classList.contains('card-color-swatches__colors')) {
          // team-design.swatches.js just populated swatches — retry the
          // auto-load now that a thumbnail is available on the swatch.
          var card2 = t.closest ? t.closest('.card-wrapper') : null;
          if (card2 && state.currentLogoUrl && logoVisibleCards.has(card2)) {
            applyLogoToCard(card2);
          }
        } else if (t.id === 'product-grid' || t.classList.contains('product-grid-container')) {
          sawCardAddition = true;
        }
      }

      // Register any newly added cards with the IntersectionObserver.
      if (sawCardAddition) observeAllLogoCards();
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  // ─── Upload events ────────────────────────────────────────────────────────
  document.addEventListener('teamLogoUploaded', function (e) {
    var result = e && e.detail;
    state.currentLogoUrl = (result && (result.urls.medium || result.urls.original)) || null;

    // Uploading a logo is just as much a "starting customization" signal
    // as typing a team name — promote the API-declared default font to
    // active so the team-text overlay (which the logo upload will
    // trigger SVG renders for) shows the configured font instead of the
    // Anton fallback. No-op if the shopper already picked a font.
    var qc = window.CollectionQuickCustomization;
    if (qc && qc.selectDefaultFont) qc.selectDefaultFont();

    applyLogoToVisibleCards();
  });

  document.addEventListener('teamLogoRemoved', function () {
    state.currentLogoUrl = null;
    clearLogoOnlyCards();
    refreshAllVisibleLogos();
  });

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    setupIntersectionObserver();
    startGridObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  NS.logo = {
    inject:              injectLogo,
    refreshVisible:      refreshAllVisibleLogos,
    applyToVisibleCards: applyLogoToVisibleCards,
  };
})();
